/**
 * @license Codistica
 *
 * Copyright (c) 2020, Codistica and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE.md file in the root directory of this source tree.
 *
 * BUILD DATE: Fri Jul 10 2020 16:51:08 GMT+0200 (Central European Summer Time)
 */
import _extends from "@babel/runtime-corejs3/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime-corejs3/helpers/esm/objectWithoutProperties";

/** @module react/components/full-screen-slide */
import React from 'react';
import resetClassNames from '../../css/reset.module.scss';
import { withOverscrollBlocker } from '../../hocs/with-overscroll-blocker.js';
import { mergeClassNames } from '../../modules/merge-class-names.js';
import { mergeStyles } from '../../modules/merge-styles.js';
import { TrackSlide } from '../track-slide/index.js';
var OverscrollBlockerDiv = withOverscrollBlocker('div');

/**
 * @typedef fullScreenSlidePropsType
 * @property {string} [direction='row'] - Slide direction.
 * @property {number} [startingPosition=0] - Slide starting position.
 * @property {number} [itemsPerView=1] - Slide items per view.
 * @property {Function} [onMount=null] - Callback for componentDidMount event.
 * @property {Function} [onNewPosition=null] - Callback for newPosition event.
 * @property {*} [children=null] - React prop.
 * @property {Object<string,*>} [style={}] - React prop.
 * @property {string} [className=''] - React prop.
 * @property {Object<string,*>} [customStyles={}] - Custom styles prop.
 * @property {Object<string,*>} [customClassNames={}] - Custom classNames prop.
 * @property {('default'|string|null)} [globalTheme='default'] - Global theme to be used.
 */

/**
 * @description A simple yet powerful full screen slide component.
 * @param {fullScreenSlidePropsType} props - Component props.
 * @returns {Object<string,*>} React component.
 */
function FullScreenSlide(props) {
  var elements = props.elements,
      children = props.children,
      customStyles = props.customStyles,
      customClassNames = props.customClassNames,
      style = props.style,
      className = props.className,
      globalTheme = props.globalTheme,
      others = _objectWithoutProperties(props, ["elements", "children", "customStyles", "customClassNames", "style", "className", "globalTheme"]);

  var globalStyles = globalTheme ? FullScreenSlide.globalStyles[globalTheme] || {} : {};
  var globalClassNames = globalTheme ? FullScreenSlide.globalClassNames[globalTheme] || {} : {};
  var mergedStyles = {
    root: mergeStyles(globalStyles.root, customStyles.root, style, {
      position: 'fixed',
      top: 0,
      left: 0,
      height: '100%',
      width: '100%'
    })
  };
  var mergedClassNames = {
    root: mergeClassNames(resetClassNames.root, globalClassNames.root, customClassNames.root, className)
  };
  return /*#__PURE__*/React.createElement(OverscrollBlockerDiv, {
    style: mergedStyles.root,
    className: mergedClassNames.root
  }, /*#__PURE__*/React.createElement(TrackSlide, _extends({}, others, {
    dimensions: {
      height: '100%',
      width: '100%'
    },
    globalTheme: null
  }), children), elements);
}

FullScreenSlide.defaultProps = {
  direction: 'row',
  startingPosition: 0,
  itemsPerView: 1,
  onMount: null,
  onNewPosition: null,
  children: null,
  elements: null,
  style: {},
  className: '',
  customStyles: {},
  customClassNames: {},
  globalTheme: 'default'
};
FullScreenSlide.globalStyles = {
  default: {
    root: {}
  }
};
FullScreenSlide.globalClassNames = {
  default: {
    root: ''
  }
};
export { FullScreenSlide };